import axios from "axios";
import Cookies from "js-cookie";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { Breadcrumb, Container } from "reactstrap";
import {
  Card,
  Input as AntInput,
  Typography,
  Space,
  Select,
  DatePicker,
  Button,
} from "antd";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import useDarkModeClass from "../../hooks/useDarkModeClass";
import { refresh } from "../../store/adminSlice";
import Loading from "../common/loading/loading";
import OrderTable from "./OrderTable";

const OrderList = () => {
  const { t } = useTranslation();
  const isDarkMode = useDarkModeClass();
  const [datas, setDatas] = useState({
    results: [],
    count: 0,
    current_page: 1,
    page_size: 10,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("status") || null;
  });
  const [dateRange, setDateRange] = useState(null);
  const [sortByDate, setSortByDate] = useState(null);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastMessage = useLocation();
  const location = useLocation();

  useEffect(() => {
    if (toastMessage.state && toastMessage?.state?.message) {
      toast.success(toastMessage.state.message);
    }
  }, [toastMessage.state]);

  useEffect(() => {
    const handler = setTimeout(
      () => {
        setDebouncedSearchTerm(searchTerm);
      },
      searchTerm ? 500 : 0,
    );

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm !== null) {
      const queryParams = new URLSearchParams(location.search);
      const prevSearch = queryParams.get("search") || "";
      const prevStatus = queryParams.get("status") || "";
      let shouldUpdateUrl = false;

      if (debouncedSearchTerm !== prevSearch) {
        if (debouncedSearchTerm) {
          queryParams.set("search", debouncedSearchTerm);
        } else {
          queryParams.delete("search");
        }
        shouldUpdateUrl = true;
      }

      if ((statusFilter || "") !== prevStatus) {
        if (statusFilter) {
          queryParams.set("status", statusFilter);
        } else {
          queryParams.delete("status");
        }
        shouldUpdateUrl = true;
      }

      if (shouldUpdateUrl) {
        queryParams.set("page", 1);
        navigate(`?${queryParams.toString()}`, { replace: true });
      }
      fetchData(
        1,
        debouncedSearchTerm,
        statusFilter,
        dateRange,
        sortByDate,
        paymentMethodFilter,
      );
    }
  }, [
    debouncedSearchTerm,
    statusFilter,
    dateRange,
    sortByDate,
    paymentMethodFilter,
  ]);

  const fetchData = async (
    page = 1,
    searchQuery = "",
    status = null,
    dates = null,
    sortDate = null,
    payMethod = null,
  ) => {
    if (isFetching) return;
    setIsFetching(true);
    setDatas((prev) => ({ ...prev, results: [] }));

    let startDate = null;
    let endDate = null;
    if (dates && dates.length === 2) {
      startDate = dates[0].format("YYYY-MM-DD");
      endDate = dates[1].format("YYYY-MM-DD");
    }

    try {
      const res = await axios.get(`/order/api/v1/orders/admin/`, {
        params: {
          page,
          page_size: 10,
          search: searchQuery,
          status: status || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          sort_by_date: sortDate || undefined,
          payment_method: payMethod || undefined,
        },
      });
      setDatas({
        ...res.data,
        current_page: res.data.current_page || page,
        results: res.data.results || [],
        count: res.data.count || 0,
        page_size: res.data.page_size || 10,
      });
    } catch (e) {
      console.log(e);
      if (e.response?.status === 401 && Cookies.get("logss")) {
        dispatch(refresh()).then(() => fetchData(page, searchQuery));
      } else if (e.response?.status === 401 && !Cookies.get("logss")) {
        navigate("/");
      } else {
        toast.error("Error occurred");
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const currentPage = parseInt(queryParams.get("page")) || 1;
    const searchQuery = queryParams.get("search") || "";
    const currentStatus = queryParams.get("status") || statusFilter;
    if (!isFetching && (!datas.results || datas.results.length === 0)) {
      setSearchTerm(searchQuery);
      setDebouncedSearchTerm(searchQuery);
      if (currentStatus !== statusFilter) {
        setStatusFilter(currentStatus);
      }
      fetchData(
        currentPage,
        searchQuery,
        currentStatus,
        dateRange,
        sortByDate,
        paymentMethodFilter,
      );
    }
  }, [location.search]);

  const handleSearch = (e) => {
    let searchValue = e.target.value;
    const validStartRegex = /^[a-zA-Z0-9]/;
    if (!validStartRegex.test(searchValue)) {
      searchValue = searchValue.replace(/^[^a-zA-Z0-9]+/, "");
    }
    setSearchTerm(searchValue);
  };

  const handlePageChange = (page) => {
    const validPage = isNaN(page) || page < 1 ? 1 : Math.floor(Number(page));
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("page", validPage);
    navigate(`?${queryParams.toString()}`, { replace: true });
    fetchData(
      validPage,
      debouncedSearchTerm || "",
      statusFilter,
      dateRange,
      sortByDate,
      paymentMethodFilter,
    );
  };

  const handleDownloadReport = async () => {
    if (isExporting) return;
    setIsExporting(true);

    let startDate = null;
    let endDate = null;
    if (dateRange && dateRange.length === 2) {
      startDate = dateRange[0].format("YYYY-MM-DD");
      endDate = dateRange[1].format("YYYY-MM-DD");
    }

    const queryParams = new URLSearchParams(location.search);
    const params = {};
    for (const [key, value] of queryParams.entries()) {
      if (key !== "page" && key !== "page_size" && value) {
        params[key] = value;
      }
    }

    if (debouncedSearchTerm || searchTerm) {
      params.search = debouncedSearchTerm || searchTerm;
    } else {
      delete params.search;
    }

    if (statusFilter) {
      params.status = statusFilter;
    } else {
      delete params.status;
    }

    if (startDate) {
      params.start_date = startDate;
    } else {
      delete params.start_date;
    }

    if (endDate) {
      params.end_date = endDate;
    } else {
      delete params.end_date;
    }

    if (sortByDate) {
      params.sort_by_date = sortByDate;
    } else {
      delete params.sort_by_date;
    }

    if (paymentMethodFilter) {
      params.payment_method = paymentMethodFilter;
    } else {
      delete params.payment_method;
    }

    try {
      const res = await axios.get(`/order/api/v1/orders/admin/export/`, {
        params,
      });
      const message =
        res?.data?.details ||
        res?.data?.detail ||
        res?.data?.message ||
        t("orders.downloadReportSuccess", "Export request sent successfully.");
      toast.success(message);
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401 && Cookies.get("logss")) {
        dispatch(refresh()).then(async () => {
          try {
            const res = await axios.get(`/order/api/v1/orders/admin/export/`, {
              params,
            });
            const message =
              res?.data?.details ||
              res?.data?.detail ||
              res?.data?.message ||
              t("orders.downloadReportSuccess", "Export request sent successfully.");
            toast.success(message);
          } catch (e) {
            const errorMsg =
              e?.response?.data?.details ||
              e?.response?.data?.detail ||
              e?.response?.data?.message ||
              t("orders.downloadReportFailed", "Failed to download report");
            toast.error(errorMsg);
          }
        });
      } else if (error.response?.status === 401 && !Cookies.get("logss")) {
        navigate("/");
      } else {
        const errorMsg =
          error?.response?.data?.details ||
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          t("orders.downloadReportFailed", "Failed to download report");
        toast.error(errorMsg);
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title={t("orders.ordersList", "Orders List")} parent={t("menu.orders", "Orders")} />
      <Container fluid={true}>
        <Card
          title={
            <Typography.Title level={4} style={{ margin: 0 }}>
              {t("orders.ordersList", "Orders List")}
            </Typography.Title>
          }
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            border: "none",
            background: isDarkMode ? "#1f1f1f" : "#ffffff",
          }}
          bodyStyle={{ padding: "0" }}
        >
          <div
            style={{
              padding: "16px 20px",
              borderBottom: isDarkMode
                ? "1px solid #303030"
                : "1px solid #f0f0f0",
            }}
          >
            <Space
              wrap
              size="middle"
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <Space wrap size="middle">
                <AntInput
                  placeholder={t("orders.searchOrders", "Search orders...")}
                  value={searchTerm}
                  onChange={handleSearch}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  allowClear
                  style={{ width: 220, borderRadius: "8px" }}
                />
                <Select
                  placeholder={t("table.columns.status", "Order Status")}
                  allowClear
                  style={{ width: 140 }}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter(val)}
                  options={[
                    { label: t("order.status.pending", "Pending"), value: "Pending" },
                    { label: t("order.status.processing", "Processing"), value: "Processing" },
                    { label: t("order.status.arrived", "Arrived"), value: "Arrived" },
                    { label: t("order.status.completed", "Completed"), value: "Completed" },
                    { label: t("order.status.refused", "Refused"), value: "Refused" },
                    { label: t("order.status.canceled", "Canceled"), value: "Canceled" },
                    { label: t("order.status.failed", "Failed"), value: "Failed" },
                    { label: t("order.status.shipping", "Shipping"), value: "Shipping" },
                  ]}
                />
                <DatePicker.RangePicker
                  style={{ borderRadius: "8px" }}
                  value={dateRange}
                  onChange={(val) => setDateRange(val)}
                />
                <Select
                  placeholder={t("orders.sortByDate", "Sort by Date")}
                  allowClear
                  style={{ width: 140 }}
                  value={sortByDate}
                  onChange={(val) => setSortByDate(val)}
                  options={[
                    { label: "Newest First", value: "desc" },
                    { label: "Oldest First", value: "asc" },
                  ]}
                />
                <Select
                  placeholder={t("orders.paymentMethod", "Payment Method")}
                  allowClear
                  style={{ width: 160 }}
                  value={paymentMethodFilter}
                  onChange={(val) => setPaymentMethodFilter(val)}
                  options={[
                    { label: "Tabby", value: "tabby" },
                    { label: "Tamara", value: "tamara" },
                    { label: "Stripe", value: "stripe" },
                  ]}
                />
              </Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={isExporting}
                onClick={handleDownloadReport}
                style={{
                  borderRadius: "8px",
                  background: "#52c41a",
                  borderColor: "#52c41a",
                }}
              >
                {t("orders.downloadReport", "Download Report")}
              </Button>
            </Space>
          </div>
          {isFetching && (!datas.results || datas.results.length === 0) ? (
            <div style={{ padding: "40px" }}>
              <Loading />
            </div>
          ) : datas?.results?.length > 0 ? (
            <div className="order-table-container">
              <OrderTable
                myData={datas}
                hideFields={[
                  "updated_at",
                  "coupon",
                  "order_products",
                  "address",
                  "postal_code",
                  "gps",
                  "phone2",
                  "country",
                  "ref_link",
                  "tax",
                  "delivery_charge",
                  "cash_on_delivery_amount",
                  "user",
                  "state",
                  "delivery_charge",
                  "shipping_address",
                  "1688_orders",
                  "ref_identifier",
                  "Imile_waybill",
                  "imile_waybill",
                ]}
                apiLink={"/order/api/v1/orders/admin/"}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#888",
              }}
            >
              {t("orders.noOrdersFound", "No orders found.")}
            </div>
          )}
        </Card>
      </Container>
      <ToastContainer />
    </Fragment>
  );
};

export default OrderList;
